import { useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import AppInput from '../atoms/AppInput';
import AppButton from '../atoms/AppButton';
import InfoHint from '../atoms/InfoHint';
import { useToast } from '../contexts/ToastContext';
import { createAddonGroup, updateAddonGroup } from '../api/addonGroupsApi';
import { formatBRL } from '../utils/currency';
import { titleCase } from '../utils/text';
import { IconImage } from '../atoms/Icon';

function Thumb({ url }) {
  return (
    <span className="group-picker-thumb">
      {url ? <img src={url} alt="" /> : <IconImage size={13} style={{ color: '#bbb' }} />}
    </span>
  );
}

/**
 * O adicional em si continua sendo criado no produto ou na categoria (mesmo
 * fluxo de sempre, via NestedItemsManager) — aqui só se escolhe produto(s) e,
 * dentre os adicionais já cadastrados neles, quais entram no grupo com a
 * regra de limite/gratuidade. Contrato confirmado pelo backend.
 */
function AddonGroupFormModal({ show, onHide, group, groups, products, categories, nextSortOrder, onSaved }) {
  const isEditing = !!group;
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [productIds, setProductIds] = useState(new Set(group?.productIds || []));
  const [addonIds, setAddonIds] = useState(new Set(group?.addons?.map((a) => a.id) || group?.addonIds || []));
  const [maxSelection, setMaxSelection] = useState(String(group?.maxSelection ?? 1));
  const [freeQuantity, setFreeQuantity] = useState(String(group?.freeQuantity ?? 0));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const toast = useToast();

  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  // Adicionais disponíveis para o grupo: os dos produtos escolhidos + os da
  // categoria de cada um (que também valem para o produto), sem repetir.
  const availableAddons = useMemo(() => {
    const seen = new Map();
    for (const productId of productIds) {
      const product = products.find((p) => p.id === productId);
      if (!product) continue;
      const category = categoryById.get(product.categoryId);
      for (const addon of [...(category?.addons || []), ...(product.addons || [])]) {
        if (!seen.has(addon.id)) seen.set(addon.id, addon);
      }
    }
    return [...seen.values()];
  }, [productIds, products, categoryById]);

  function toggleProduct(productId) {
    setProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
    setFieldErrors((p) => ({ ...p, productIds: null }));
  }

  function toggleAddon(addonId) {
    setAddonIds((prev) => {
      const next = new Set(prev);
      if (next.has(addonId)) next.delete(addonId);
      else next.add(addonId);
      return next;
    });
  }

  function validate() {
    const errs = {};
    const trimmedName = name.trim();
    if (!trimmedName) {
      errs.name = 'Informe o nome do grupo.';
    } else {
      // O backend só bloqueia duplicidade quando o grupo compartilha produto
      // com outro de mesmo nome — checagem local espelha essa regra para dar
      // feedback antes de bater na API.
      const duplicate = groups.some((g) => (
        g.id !== group?.id
        && g.name.trim().toLowerCase() === trimmedName.toLowerCase()
        && (g.productIds || []).some((id) => productIds.has(id))
      ));
      if (duplicate) errs.name = 'Já existe um grupo com este nome para um dos produtos escolhidos.';
    }

    if (productIds.size === 0) errs.productIds = 'Escolha ao menos um produto.';

    const max = parseInt(maxSelection, 10);
    if (!Number.isInteger(max) || max < 1) {
      errs.maxSelection = 'Informe um número inteiro maior ou igual a 1.';
    }

    const free = parseInt(freeQuantity, 10);
    if (!Number.isInteger(free) || free < 0) {
      errs.freeQuantity = 'Informe um número inteiro maior ou igual a 0.';
    } else if (Number.isInteger(max) && free > max) {
      errs.freeQuantity = 'Não pode ser maior que o limite máximo de seleção.';
    }

    setFieldErrors(errs);
    return !Object.keys(errs).length;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);
    const payload = {
      name: titleCase(name.trim()),
      description: description.trim() || null,
      productIds: [...productIds],
      addonIds: [...addonIds],
      maxSelection: parseInt(maxSelection, 10),
      freeQuantity: parseInt(freeQuantity, 10),
    };
    try {
      if (isEditing) {
        await updateAddonGroup(group.id, payload);
        toast.success('Grupo de adicionais atualizado com sucesso!');
      } else {
        await createAddonGroup({ ...payload, sortOrder: nextSortOrder() });
        toast.success('Grupo de adicionais criado com sucesso!');
      }
      onSaved();
      onHide();
    } catch (e2) {
      setError(e2.message);
      toast.error(e2.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered scrollable size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Editar grupo de adicionais' : 'Novo grupo de adicionais'}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '20px 24px' }}>
        {error && (
          <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: 13, borderRadius: 8 }}>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} id="addon-group-form" noValidate>
          <AppInput
            label="Nome"
            value={name}
            onChange={(e) => { setName(e.target.value); setFieldErrors((p) => ({ ...p, name: null })); }}
            placeholder="Ex: Cremes"
            error={fieldErrors.name}
            autoCapitalize="sentences"
            autoCorrect="off"
            autoComplete="off"
            enterKeyHint="next"
            required
          />

          <Form.Group className="mb-3">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Escolha os cremes do seu açaí"
              autoCapitalize="sentences"
              enterKeyHint="next"
            />
          </Form.Group>

          <div className="form-row-split">
            <div className="flex-grow-1">
              <AppInput
                label="Limite máximo de seleção"
                type="number"
                inputMode="numeric"
                min={1}
                value={maxSelection}
                onChange={(e) => { setMaxSelection(e.target.value); setFieldErrors((p) => ({ ...p, maxSelection: null })); }}
                error={fieldErrors.maxSelection}
                enterKeyHint="next"
                required
              />
            </div>
            <div className="flex-grow-1">
              <AppInput
                label="Itens gratuitos"
                type="number"
                inputMode="numeric"
                min={0}
                value={freeQuantity}
                onChange={(e) => { setFreeQuantity(e.target.value); setFieldErrors((p) => ({ ...p, freeQuantity: null })); }}
                error={fieldErrors.freeQuantity}
                enterKeyHint="done"
                required
              />
            </div>
          </div>
          <InfoHint style={{ marginTop: -8 }}>
            O cliente escolhe até o limite máximo; os primeiros itens gratuitos não somam ao valor do pedido.
          </InfoHint>

          <Form.Group className="mb-3 mt-3">
            <Form.Label>Produtos</Form.Label>
            {fieldErrors.productIds && (
              <div className="mb-1" style={{ fontSize: 12, color: '#e74c3c' }}>{fieldErrors.productIds}</div>
            )}
            <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 8 }}>
              {products.length === 0 ? (
                <p className="items-summary-empty mb-0">Cadastre produtos no cardápio primeiro.</p>
              ) : products.map((product) => (
                <Form.Check
                  key={product.id}
                  type="checkbox"
                  id={`group-product-${product.id}`}
                  checked={productIds.has(product.id)}
                  onChange={() => toggleProduct(product.id)}
                  className="mb-1"
                  label={(
                    <span className="d-flex align-items-center gap-2">
                      <Thumb url={product.imageUrl} />
                      <span>{product.name}</span>
                    </span>
                  )}
                />
              ))}
            </div>
            <InfoHint>O grupo (limite e gratuidade) vale para os produtos marcados aqui.</InfoHint>
          </Form.Group>

          <Form.Group>
            <Form.Label>Adicionais do grupo</Form.Label>
            {productIds.size === 0 ? (
              <p className="items-summary-empty">Escolha ao menos um produto para ver os adicionais disponíveis.</p>
            ) : availableAddons.length === 0 ? (
              <p className="items-summary-empty">
                Nenhum adicional cadastrado nesses produtos ainda. Cadastre em Cardápio, editando o produto ou a categoria.
              </p>
            ) : (
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 8 }}>
                {availableAddons.map((addon) => (
                  <Form.Check
                    key={addon.id}
                    type="checkbox"
                    id={`group-addon-${addon.id}`}
                    checked={addonIds.has(addon.id)}
                    onChange={() => toggleAddon(addon.id)}
                    className="mb-1"
                    label={(
                      <span className="d-flex align-items-center justify-content-between" style={{ gap: 8 }}>
                        <span className="d-flex align-items-center gap-2">
                          <Thumb url={addon.imageUrl} />
                          <span>{addon.name}</span>
                        </span>
                        <span style={{ color: '#888', flexShrink: 0 }}>{addon.isPaid === false ? 'Gratuito' : formatBRL(Number(addon.price))}</span>
                      </span>
                    )}
                  />
                ))}
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <AppButton variant="ghost" onClick={onHide}>Fechar</AppButton>
        <AppButton type="submit" form="addon-group-form" variant="primary" disabled={saving}>
          {saving ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Criar grupo'}
        </AppButton>
      </Modal.Footer>
    </Modal>
  );
}

export default AddonGroupFormModal;
